import * as React from "react";
import { motion, type HTMLMotionProps, type Transition } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Shared transition for row enter/exit/reorder. Tight (200ms ease-out) so
 * dense tables don't feel sluggish.
 */
export const rowTransition: Transition = { duration: 0.2, ease: "easeOut" };

export const rowAnim = {
  initial: { opacity: 0, y: -8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, x: -20 },
  transition: rowTransition,
};

/**
 * `motion.tr` pre-styled with shadcn's TableRow baseline so it can drop into
 * any TanStack table without losing hover/selected styling. Pass `index` to
 * stagger exit animations for bulk actions.
 */
type MotionTableRowProps = HTMLMotionProps<"tr"> & {
  /** Stagger key for cascade exits (50ms × index, capped at 10). */
  index?: number;
};

export const MotionTableRow = React.forwardRef<
  HTMLTableRowElement,
  MotionTableRowProps
>(function MotionTableRow({ className, index, transition, ...props }, ref) {
  const delay = index != null ? Math.min(index, 20) * 0.03 : 0;
  return (
    <motion.tr
      ref={ref}
      layout
      initial={rowAnim.initial}
      animate={rowAnim.animate}
      exit={rowAnim.exit}
      transition={{ ...rowTransition, ...transition, delay }}
      className={cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      style={{ willChange: "transform, opacity", ...props.style }}
      {...props}
    />
  );
});

/**
 * `motion.li` pre-styled for divide-y lists (notifications, members, tasks).
 */
type MotionListItemProps = HTMLMotionProps<"li"> & { index?: number };

export const MotionListItem = React.forwardRef<
  HTMLLIElement,
  MotionListItemProps
>(function MotionListItem({ className, index, transition, ...props }, ref) {
  const delay = index != null ? Math.min(index, 20) * 0.03 : 0;
  return (
    <motion.li
      ref={ref}
      layout
      initial={rowAnim.initial}
      animate={rowAnim.animate}
      exit={rowAnim.exit}
      transition={{ ...rowTransition, ...transition, delay }}
      className={className}
      style={{ willChange: "transform, opacity", ...props.style }}
      {...props}
    />
  );
});

/**
 * Same as MotionListItem but as a div, for non-list containers like
 * TaskList (which uses divs not <li>) and ActivityTimeline.
 */
type MotionDivProps = HTMLMotionProps<"div"> & { index?: number };

export const MotionRow = React.forwardRef<HTMLDivElement, MotionDivProps>(
  function MotionRow({ className, index, transition, ...props }, ref) {
    const delay = index != null ? Math.min(index, 20) * 0.03 : 0;
    return (
      <motion.div
        ref={ref}
        layout
        initial={rowAnim.initial}
        animate={rowAnim.animate}
        exit={rowAnim.exit}
        transition={{ ...rowTransition, ...transition, delay }}
        className={className}
        style={{ willChange: "transform, opacity", ...props.style }}
        {...props}
      />
    );
  },
);
